// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Menu, MessageSquare, MoreVertical, LogOut, LogIn, Building2, X } from 'lucide-react'
import { useAuth, portalBrandLabel } from '../auth/AuthContext'
import NotificationBell from '../components/NotificationBell'
import { usePharmacy } from '../auth/PharmacyContext'

interface Props {
  onToggleSidebar: () => void
  sidebarOpen: boolean       // for aria-expanded
  onOpenFeedback: () => void
}

/**
 * Thin top bar (40px). Lives above all routed content. Contents:
 *   - Hamburger toggle (always visible; collapses sidebar on desktop,
 *     shows overlay on tablet/mobile)
 *   - Brand text (role-aware: "Pharmacy Portal" / "Provider Portal" / "Patient Portal")
 *   - Right side: feedback button, account kebab menu
 */
// D-SOC-9 L-1 (TL condition, 2026-09-25): an ALLOW-list, mirroring GET /api/notifications'
// RequireOneOf exactly (NotificationFunctions.cs:247/291/335/365). This was a deny-list —
// `role !== 'manufacturer'` — and the comment below records why that exclusion exists: mounting
// the bell for a role the endpoint rejects produced a polling storm (D-MFR-1).
//
// A deny-list makes every NEW external role a latent storm that someone must remember to add.
// 'facility' proved it: the role shipped, the shell was never mounted for it, and when D-SOC-9
// S1 finally mounted it the bell polled an endpoint that 403s without #2708. Defence in depth
// means the client does not depend solely on the server having been updated first.
//
// Adding a role here without adding it to RequireOneOf reintroduces the storm, so these two
// lists must move together.
const NOTIFICATION_ROLES = ['pharmacy_staff', 'provider', 'provider_nurse', 'patient', 'admin', 'support', 'facility']

export default function TopBar({ onToggleSidebar, sidebarOpen, onOpenFeedback }: Props) {
  const { me, role, signOut } = useAuth()
  const { mode, activePharmacy, isOverride, exitPharmacy } = usePharmacy()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Click-outside to close menu
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuRef.current) return
      if (!menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    if (menuOpen) document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [menuOpen])

  async function handleSignOut() {
    setMenuOpen(false)
    await signOut()
    navigate('/login')  // basename prefixes to /p/{slug}/login for a pharmacy session
  }

  function handleExitPharmacy() {
    exitPharmacy()
    navigate('/admin/pharmacies')
  }

  return (
    <header style={topBarStyle}>
      <button
        onClick={onToggleSidebar}
        aria-label="Toggle navigation menu"
        aria-expanded={sidebarOpen}
        aria-controls="appshell-sidebar"
        style={hamburgerStyle}
        title="Menu"
      >
        <Menu size={18} aria-hidden="true" />
      </button>

      <Link to="/" style={brandLinkStyle}>{portalBrandLabel(role)}</Link>

      {/* WI-893: SaaS-mode marker. Shown when an Owl admin has not yet
          entered any pharmacy. Communicates "you're in cross-tenant view". */}
      {me?.authenticated && mode === 'saas' && (
        <span style={saasModeBadgeStyle} title="SaaS administration view — no pharmacy selected">
          SaaS view
        </span>
      )}

      {/* WI-893 + WI-899 + WI-902: active-pharmacy banner.
          - SaaS-admin override → chip with X to exit back to SaaS view
          - Home-pharmacy user  → chip without exit (they have no SaaS view)
          - Shows the pharmacy's logo if set, otherwise a generic icon. */}
      {me?.authenticated && mode === 'pharmacy' && activePharmacy && (
        <div style={pharmChipStyle} role="status" aria-live="polite">
          {activePharmacy.logo_url ? (
            <img
              src={activePharmacy.logo_url}
              alt=""
              aria-hidden="true"
              style={pharmChipLogoStyle}
            />
          ) : (
            <Building2 size={13} aria-hidden="true" />
          )}
          <span style={pharmChipNameStyle}>
            {activePharmacy.name || activePharmacy.slug || 'Loading…'}
          </span>
          {isOverride && (
            <button
              onClick={handleExitPharmacy}
              style={pharmChipExitStyle}
              aria-label="Exit pharmacy view, return to SaaS administration"
              title="Exit to SaaS view"
            >
              <X size={12} aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      <div style={rightGroupStyle}>
        {/* WI-902: Pharmacy logo on the right side, displayed prominently
            when in pharmacy mode and the pharmacy has a logo configured.
            Falls back to nothing when no logo, so the topbar stays clean. */}
        {me?.authenticated && mode === 'pharmacy' && activePharmacy?.logo_url && (
          <img
            src={activePharmacy.logo_url}
            alt={`${activePharmacy.name} logo`}
            style={topRightLogoStyle}
          />
        )}

        {/* Notifications. Excludes 'manufacturer': that external role is not authorized on
            GET /api/notifications (403 wrong_role), and mounting the bell for it produced a
            polling storm (D-MFR-1). Defense in depth — the role should already be redirected
            out of the pharmacy shell before reaching here.

            D-SUPPORT-7: this used to require mode === 'pharmacy', which silently excluded the
            ENTIRE support tier and every platform admin — they are unbound and therefore always
            in SaaS mode, so they had no bell at all. Support case notifications were being
            written correctly to dbo.notifications and were structurally invisible: no surface
            rendered them. GET /api/notifications already authorizes "admin" and "support", so
            the gate was the only thing in the way. The page at /notifications is a SEPARATE
            surface — in SaaS mode it shows platform ALERTS, not this personal inbox. */}
        {me?.authenticated && NOTIFICATION_ROLES.includes(role as string) && <NotificationBell />}

        {/* Feedback */}
        <button
          onClick={onOpenFeedback}
          aria-label="Send feedback"
          style={iconBtnStyle}
          title="Send feedback"
        >
          <MessageSquare size={16} aria-hidden="true" />
        </button>

        {/* Account menu (or sign-in link if anonymous) */}
        {me?.authenticated ? (
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Account menu"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              style={iconBtnStyle}
              title="Account"
            >
              <MoreVertical size={16} aria-hidden="true" />
            </button>
            {menuOpen && (
              <div role="menu" style={menuStyle}>
                <div style={menuHeaderStyle}>
                  <div style={menuNameStyle}>{me.display_name ?? me.email}</div>
                  <div style={menuEmailStyle}>{me.email}</div>
                </div>
                <button onClick={handleSignOut} role="menuitem" style={menuItemStyle}>
                  <LogOut size={14} aria-hidden="true" />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <Link to="/login" style={signInLinkStyle}>
            <LogIn size={14} aria-hidden="true" />
            <span>Sign in</span>
          </Link>
        )}
      </div>
    </header>
  )
}

const topBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  height: 48,
  padding: '0 14px',
  background: 'var(--color-surface, #fff)',
  borderBottom: '1px solid var(--color-border)',
  position: 'sticky',
  top: 0,
  zIndex: 30,
}

const hamburgerStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center',
  width: 32, height: 32,
  background: 'transparent',
  border: 'none',
  borderRadius: 'var(--radius-sm, 6px)',
  cursor: 'pointer',
  color: 'var(--color-text)',
}

const brandLinkStyle: React.CSSProperties = {
  fontWeight: 600, fontSize: 14,
  color: 'var(--color-text)',
  textDecoration: 'none',
}

// WI-893
const saasModeBadgeStyle: React.CSSProperties = {
  marginLeft: 12,
  fontSize: 11,
  fontWeight: 500,
  padding: '2px 8px',
  background: 'var(--color-surface-mute, rgba(0,0,0,0.04))',
  color: 'var(--color-text-mute)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-pill, 999px)',
  letterSpacing: 0.2,
}

const pharmChipStyle: React.CSSProperties = {
  marginLeft: 12,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 4px 3px 8px',
  background: 'var(--color-primary-soft, rgba(0,113,180,0.10))',
  color: 'var(--color-primary, #006699)',
  border: '1px solid var(--color-primary-soft-border, rgba(0,113,180,0.25))',
  borderRadius: 'var(--radius-pill, 999px)',
  fontSize: 12,
  fontWeight: 500,
  maxWidth: 280,
}

const pharmChipNameStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 200,
}

const pharmChipLogoStyle: React.CSSProperties = {
  width: 16,
  height: 16,
  borderRadius: 3,
  objectFit: 'contain',
  background: 'rgba(255,255,255,0.6)',
}

const topRightLogoStyle: React.CSSProperties = {
  height: 26,
  maxWidth: 110,
  objectFit: 'contain',
  marginRight: 6,
  borderRadius: 3,
}

const pharmChipExitStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18, height: 18,
  background: 'transparent',
  border: 'none',
  borderRadius: '50%',
  cursor: 'pointer',
  color: 'currentColor',
  opacity: 0.7,
}

const rightGroupStyle: React.CSSProperties = {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}

const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center',
  width: 32, height: 32,
  background: 'transparent',
  border: 'none',
  borderRadius: 'var(--radius-sm, 6px)',
  cursor: 'pointer',
  color: 'var(--color-text-soft)',
}

const signInLinkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center', gap: 6,
  padding: '6px 12px',
  background: 'var(--color-primary)',
  color: '#fff',
  textDecoration: 'none',
  borderRadius: 'var(--radius-sm, 6px)',
  fontSize: 13,
  fontWeight: 500,
}

const menuStyle: React.CSSProperties = {
  position: 'absolute',
  right: 0, top: 'calc(100% + 4px)',
  minWidth: 220,
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md, 8px)',
  boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.08))',
  zIndex: 40,
  overflow: 'hidden',
}

const menuHeaderStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderBottom: '1px solid var(--color-border)',
}

const menuNameStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600,
  color: 'var(--color-text)',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}

const menuEmailStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: 'var(--color-text-mute)',
  marginTop: 2,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}

const menuItemStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '10px 14px',
  background: 'transparent',
  border: 'none',
  fontSize: 13,
  color: 'var(--color-text)',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
}
